import { createFileRoute } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  ScrollArea,
  Select,
  Stack,
  Tabs,
  Text,
  Textarea,
  TextInput,
  ThemeIcon,
  Tooltip,
  TypographyStylesProvider,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBookmark,
  IconCode,
  IconEye,
  IconHistory,
  IconInfoCircle,
  IconSend,
  IconStar,
  IconStarFilled,
  IconTrash,
  IconUsers,
  IconVariable,
} from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import { httpRequest } from '@/shared/api/client';
import { JsonApiHttpError } from '@/shared/api/jsonapi/errors';
import { PageHeader } from '@/shared/components/PageHeader';

export const Route = createFileRoute('/_app/admin/send-communications')({ component: SendCommsPage });

interface PreviewResult { audience: string; recipientCount: number }
interface SendResult { sent: number; failed: number; totalRecipients: number; errors?: Array<{ recipient: string; errorMessage: string }>; message?: string }

interface SavedTemplate { id: string; name: string; subject: string; body: string; audience: string; createdAt: string }
interface SendHistoryEntry { id: string; subject: string; audience: string; sent: number; failed: number; at: string }

const TEMPLATES_KEY = 'comm-templates';
const HISTORY_KEY = 'comm-history';
const HISTORY_MAX = 10;

function loadTemplates(): SavedTemplate[] {
  try { return JSON.parse(localStorage.getItem(TEMPLATES_KEY) ?? '[]'); } catch { return []; }
}
function saveTemplates(t: SavedTemplate[]) {
  try { localStorage.setItem(TEMPLATES_KEY, JSON.stringify(t)); } catch { /* ignore */ }
}
function loadHistory(): SendHistoryEntry[] {
  try { return JSON.parse(localStorage.getItem(HISTORY_KEY) ?? '[]'); } catch { return []; }
}
function pushHistory(e: Omit<SendHistoryEntry, 'id' | 'at'>) {
  const next: SendHistoryEntry = { ...e, id: crypto.randomUUID?.() ?? `${Date.now()}`, at: new Date().toISOString() };
  const list = loadHistory();
  list.unshift(next);
  try { localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(0, HISTORY_MAX))); } catch { /* ignore */ }
}

/** Sample data per preview con variabili sostituite. */
const SAMPLE_DATA: Record<string, string> = {
  firstName: 'Mario',
  lastName: 'Rossi',
  email: 'mario.rossi@esempio.it',
  tenantName: 'Acme Spa',
  appName: 'MaeFwk10',
};

const VARIABLES = [
  { key: 'firstName', label: 'Nome' },
  { key: 'lastName', label: 'Cognome' },
  { key: 'email', label: 'Email' },
  { key: 'tenantName', label: 'Nome tenant' },
  { key: 'appName', label: 'Nome app' },
];

function applyVariables(text: string, data: Record<string, string>): string {
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key: string) => data[key] ?? `{{${key}}}`);
}

function SendCommsPage() {
  const [audience, setAudience] = useState<string>('all');
  const [customAudience, setCustomAudience] = useState<string>('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  const [templates, setTemplates] = useState<SavedTemplate[]>([]);
  const [history, setHistory] = useState<SendHistoryEntry[]>([]);
  const [saveTplOpen, saveTplCtrl] = useDisclosure(false);
  const [tplName, setTplName] = useState('');

  useEffect(() => {
    setTemplates(loadTemplates());
    setHistory(loadHistory());
  }, []);

  const effectiveAudience = audience === 'custom' ? customAudience : audience;

  const previewQ = useQuery({
    queryKey: ['comm-preview', effectiveAudience],
    enabled: Boolean(effectiveAudience),
    queryFn: () => httpRequest<PreviewResult>(
      `/api/admin/send-communications/preview-audience?audience=${encodeURIComponent(effectiveAudience)}`,
      { endpointType: 'plain' }
    ),
  });

  const previewSubject = useMemo(() => applyVariables(subject, SAMPLE_DATA), [subject]);
  const previewBody = useMemo(() => DOMPurify.sanitize(applyVariables(body, SAMPLE_DATA)), [body]);

  const insertVariable = (key: string) => {
    setBody((b) => b + `{{${key}}}`);
  };

  const onSend = async () => {
    setBusy(true);
    try {
      const res = await httpRequest<SendResult>('/api/admin/send-communications', {
        method: 'POST',
        body: { audience: effectiveAudience, subject, body },
        endpointType: 'plain',
      });
      pushHistory({ subject, audience: effectiveAudience, sent: res.sent, failed: res.failed });
      setHistory(loadHistory());
      if (res.failed > 0) {
        notifications.show({
          color: 'orange',
          title: `Inviati ${res.sent}, falliti ${res.failed}`,
          message: res.errors?.[0] ? `Es: ${res.errors[0].recipient}: ${res.errors[0].errorMessage}` : 'Vedi log server',
        });
      } else {
        notifications.show({ color: 'green', title: 'Comunicazione inviata', message: `Recipient: ${res.sent}` });
      }
      setSubject(''); setBody('');
    } catch (err) {
      const msg = err instanceof JsonApiHttpError ? (err.errors[0]?.detail ?? err.message) : err instanceof Error ? err.message : 'Errore';
      notifications.show({ color: 'red', title: 'Invio fallito', message: msg });
    } finally {
      setBusy(false);
    }
  };

  const saveTemplate = () => {
    if (!tplName.trim() || !subject.trim() || !body.trim()) return;
    const next: SavedTemplate = {
      id: crypto.randomUUID?.() ?? `${Date.now()}`,
      name: tplName.trim(),
      subject,
      body,
      audience: effectiveAudience,
      createdAt: new Date().toISOString(),
    };
    const updated = [...templates, next];
    saveTemplates(updated);
    setTemplates(updated);
    setTplName('');
    saveTplCtrl.close();
    notifications.show({ color: 'green', message: 'Template salvato' });
  };

  const applyTemplate = (t: SavedTemplate) => {
    setSubject(t.subject);
    setBody(t.body);
    setAudience(t.audience.startsWith('role:') || t.audience === 'all' ? t.audience : 'custom');
    if (t.audience.startsWith('user-ids:') || (!t.audience.startsWith('role:') && t.audience !== 'all')) {
      setCustomAudience(t.audience);
    }
    notifications.show({ color: 'blue', message: `Template "${t.name}" caricato` });
  };

  const deleteTemplate = (id: string) => {
    const updated = templates.filter((t) => t.id !== id);
    saveTemplates(updated);
    setTemplates(updated);
  };

  return (
    <Stack maw={1100}>
      <PageHeader
        title="Invia comunicazione"
        description={
          <>Email broadcast a un'audience del tenant. Supporta variabili <code>{'{{firstName}}'}</code>, template salvati e preview HTML.</>
        }
      />

      {templates.length > 0 && (
        <Card withBorder padding="md" radius="md">
          <Group gap={6} mb="xs">
            <IconStarFilled size={14} color="var(--mantine-color-yellow-6)" />
            <Text size="sm" fw={600}>Template salvati</Text>
          </Group>
          <Group gap="xs">
            {templates.map((t) => (
              <Badge
                key={t.id}
                variant="light"
                color="grape"
                size="lg"
                rightSection={
                  <ThemeIcon
                    size="xs"
                    variant="transparent"
                    color="gray"
                    onClick={(e) => { e.stopPropagation(); deleteTemplate(t.id); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <IconTrash size={10} />
                  </ThemeIcon>
                }
                style={{ cursor: 'pointer' }}
                onClick={() => applyTemplate(t)}
              >
                {t.name}
              </Badge>
            ))}
          </Group>
        </Card>
      )}

      <Card withBorder padding="lg" radius="md">
        <Stack>
          <Select
            label="Destinatari"
            value={audience}
            onChange={(v) => setAudience(v ?? 'all')}
            data={[
              { value: 'all', label: 'Tutti gli utenti del tenant' },
              { value: 'role:Admin', label: 'Solo Admin' },
              { value: 'role:User', label: 'Solo User base' },
              { value: 'role:HR', label: 'Solo HR' },
              { value: 'custom', label: 'Custom (vedi sotto)' },
            ]}
          />
          {audience === 'custom' && (
            <TextInput
              label="Audience custom"
              description="Sintassi: 'role:NomeRuolo' oppure 'user-ids:id1,id2,id3'"
              value={customAudience}
              onChange={(e) => setCustomAudience(e.currentTarget.value)}
              placeholder="role:CapoArea oppure user-ids:1,2,3"
            />
          )}
          <Group>
            <Badge leftSection={<IconUsers size={12} />} variant="light" color={previewQ.data?.recipientCount ? 'blue' : 'gray'} size="lg">
              {previewQ.isLoading ? '...' : `${previewQ.data?.recipientCount ?? 0} destinatari`}
            </Badge>
            <Text size="xs" c="dimmed">Anteprima audience (BE preview)</Text>
          </Group>

          <TextInput
            label="Oggetto"
            required
            value={subject}
            onChange={(e) => setSubject(e.currentTarget.value)}
          />

          <Group gap={4}>
            <ThemeIcon variant="light" color="grape" size="sm"><IconVariable size={12} /></ThemeIcon>
            <Text size="xs" c="dimmed">Variabili disponibili (click per inserire):</Text>
            {VARIABLES.map((v) => (
              <Tooltip key={v.key} label={`Esempio: ${SAMPLE_DATA[v.key]}`}>
                <Badge
                  variant="light"
                  color="grape"
                  size="sm"
                  style={{ cursor: 'pointer', fontFamily: 'monospace', textTransform: 'none' }}
                  onClick={() => insertVariable(v.key)}
                >
                  {`{{${v.key}}}`}
                </Badge>
              </Tooltip>
            ))}
          </Group>

          <Tabs defaultValue="edit">
            <Tabs.List>
              <Tabs.Tab value="edit" leftSection={<IconCode size={14} />}>Editor</Tabs.Tab>
              <Tabs.Tab value="preview" leftSection={<IconEye size={14} />}>Preview</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="edit" pt="md">
              <Textarea
                label="Corpo (HTML)"
                required
                autosize
                minRows={10}
                value={body}
                onChange={(e) => setBody(e.currentTarget.value)}
                placeholder="<h2>Ciao {{firstName}}</h2><p>Messaggio per {{tenantName}}…</p>"
                styles={{ input: { fontFamily: 'monospace' } }}
              />
            </Tabs.Panel>
            <Tabs.Panel value="preview" pt="md">
              <Card withBorder padding="md" radius="md" bg="var(--mantine-color-default-hover)">
                <Text size="xs" c="dimmed" mb="xs">
                  Preview con sample data — sostituisce <code>{'{{firstName}}'}</code> → <strong>{SAMPLE_DATA.firstName}</strong>, ecc.
                </Text>
                <Card withBorder padding="md" radius="md" bg="var(--mantine-color-body)">
                  <Text size="xs" c="dimmed">Oggetto</Text>
                  <Text fw={600} mb="md">{previewSubject || '(vuoto)'}</Text>
                  <Text size="xs" c="dimmed">Corpo</Text>
                  {previewBody ? (
                    <TypographyStylesProvider>
                      <div dangerouslySetInnerHTML={{ __html: previewBody }} />
                    </TypographyStylesProvider>
                  ) : (
                    <Text c="dimmed" size="sm">(vuoto)</Text>
                  )}
                </Card>
              </Card>
            </Tabs.Panel>
          </Tabs>

          <Group justify="space-between">
            <Button
              variant="subtle"
              leftSection={<IconBookmark size={14} />}
              onClick={saveTplCtrl.open}
              disabled={!subject.trim() || !body.trim()}
            >
              Salva come template
            </Button>
            <Button
              leftSection={<IconSend size={16} />}
              loading={busy}
              disabled={!subject || !body || !effectiveAudience || (previewQ.data?.recipientCount ?? 0) === 0}
              onClick={() => void onSend()}
            >
              Invia
            </Button>
          </Group>
        </Stack>
      </Card>

      <Alert color="blue" icon={<IconInfoCircle size={18} />} variant="light">
        Audience supportate: tutti, ruolo specifico (<code>role:NomeRuolo</code>), lista ID utenti (<code>user-ids:1,2,3</code>).
        Le variabili vengono sostituite dal BE prima dell'invio.
      </Alert>

      {history.length > 0 && (
        <Card withBorder padding={0} radius="md">
          <Group p="md" pb="xs">
            <ThemeIcon variant="light" color="gray" radius="md" size="sm"><IconHistory size={14} /></ThemeIcon>
            <Text fw={600}>Invii recenti</Text>
            <Badge variant="light" color="gray">{history.length}</Badge>
          </Group>
          <ScrollArea>
            <Stack gap="xs" px="md" pb="md">
              {history.map((h) => (
                <Card key={h.id} withBorder padding="xs" radius="sm">
                  <Group justify="space-between" wrap="nowrap">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={500} lineClamp={1}>{h.subject}</Text>
                      <Group gap={6}>
                        <Text size="xs" c="dimmed">{h.audience}</Text>
                        <Text size="xs" c="dimmed">·</Text>
                        <Text size="xs" c="dimmed">{dayjs(h.at).format('DD/MM HH:mm')}</Text>
                      </Group>
                    </div>
                    <Group gap={4}>
                      <Badge size="sm" color="green" variant="light">{h.sent} inviate</Badge>
                      {h.failed > 0 && <Badge size="sm" color="red" variant="light">{h.failed} fallite</Badge>}
                    </Group>
                  </Group>
                </Card>
              ))}
            </Stack>
          </ScrollArea>
        </Card>
      )}

      <Modal opened={saveTplOpen} onClose={saveTplCtrl.close} title="Salva template" centered>
        <Stack>
          <Text size="sm" c="dimmed">
            Oggetto e corpo correnti saranno salvati come template riutilizzabile (solo locale, su questo browser).
          </Text>
          <TextInput
            label="Nome template"
            placeholder="Es. Benvenuto nuovo utente"
            value={tplName}
            onChange={(e) => setTplName(e.currentTarget.value)}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={saveTplCtrl.close}>Annulla</Button>
            <Button leftSection={<IconStar size={14} />} onClick={saveTemplate} disabled={!tplName.trim()}>
              Salva
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
